import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, XCircle, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export type ToastType = 'success' | 'error';

interface ToastProps {
  message: string;
  type: ToastType;
  onClose: () => void;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose, duration = 5000 }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  return (
    <>
      {/* Mobile: full-width bar sliding up from the bottom */}
      <motion.div
        initial={{ opacity: 0, y: 80 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 80 }}
        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        className={`sm:hidden fixed bottom-0 left-0 right-0 z-[9999] px-4 py-3 shadow-2xl border-t-2 ${
          type === 'success'
            ? 'bg-gradient-to-r from-green-500 to-emerald-600 border-green-300'
            : 'bg-gradient-to-r from-red-500 to-rose-600 border-red-300'
        }`}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
      >
        <div className="flex items-start gap-3 max-w-lg mx-auto">
          <div className="flex-shrink-0 mt-0.5">
            {type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 text-white" />
            ) : (
              <XCircle className="w-5 h-5 text-white" />
            )}
          </div>
          <p className="text-white font-medium text-sm flex-1 leading-snug">{message}</p>
          <button
            onClick={onClose}
            className="flex-shrink-0 text-white hover:bg-white/20 rounded-lg p-1 transition-colors"
            aria-label="Close notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </motion.div>

      {/* Desktop: centered card dropping from the top */}
      <motion.div
        initial={{ opacity: 0, y: -50, x: '-50%' }}
        animate={{ opacity: 1, y: 0, x: '-50%' }}
        exit={{ opacity: 0, y: -50, x: '-50%' }}
        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        className={`hidden sm:flex fixed top-20 left-1/2 z-[9999] max-w-md w-full px-6 py-4 rounded-2xl shadow-2xl border-2 ${
          type === 'success'
            ? 'bg-gradient-to-r from-green-500 to-emerald-600 border-green-300'
            : 'bg-gradient-to-r from-red-500 to-rose-600 border-red-300'
        }`}
      >
        <div className="flex items-center gap-3 w-full">
          <div className="flex-shrink-0">
            {type === 'success' ? (
              <CheckCircle2 className="w-6 h-6 text-white" />
            ) : (
              <XCircle className="w-6 h-6 text-white" />
            )}
          </div>
          <p className="text-white font-medium text-base flex-1">{message}</p>
          <button
            onClick={onClose}
            className="flex-shrink-0 text-white hover:bg-white/20 rounded-lg p-1 transition-colors"
            aria-label="Close notification"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </motion.div>
    </>
  );
};

interface ToastContainerProps {
  toasts: Array<{ id: string; message: string; type: ToastType }>;
  removeToast: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, removeToast }) => {
  return createPortal(
    <AnimatePresence>
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </AnimatePresence>,
    document.body
  );
};

export default Toast;
